import type { ReactNode } from 'react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { BarChart } from '@/shared/components/Charts/BarChart';
import {
  useBriefing,
  useLeadScores,
  useCallOptimizer,
  useNicheIntelligence,
  useAnomalies,
  useScrapeAdvisor,
  useDeals,
  useDealScores,
} from '../../hooks/useAIAnalytics';
import { STAGE_LABELS, STAGE_TONES } from '@/features/deals/constants';
import type { AnomalyRow, LeadScore, NicheRow, ScrapeNiche } from '../../types';
import type { Deal } from '@/features/deals/types';

function scoreTone(score: number): BadgeTone {
  return score >= 70 ? 'success' : score >= 40 ? 'warning' : 'neutral';
}

/** Small query-state wrapper to keep sections DRY (reuses Spinner/EmptyState). */
function Block({
  isLoading,
  isError,
  isEmpty,
  emptyTitle,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  emptyTitle: string;
  children: ReactNode;
}) {
  if (isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;
  if (isError) return <EmptyState title="Failed to load" description="Check your connection or try refreshing." />;
  if (isEmpty) return <EmptyState title={emptyTitle} description="There isn't enough data yet to compute this." />;
  return <>{children}</>;
}

// ─── Daily Briefing ───────────────────────────────────────────────────────────
export function BriefingCard() {
  const { data = [], isLoading, isError } = useBriefing();
  return (
    <Card>
      <CardHeader title="Daily Briefing" />
      <CardBody>
        <Block isLoading={isLoading} isError={isError} isEmpty={data.length === 0} emptyTitle="No briefing items">
          <ul className="flex flex-col gap-2">
            {data.map((b, i) => (
              <li key={i} className="flex items-start gap-2 rounded-DEFAULT border border-outline bg-surface px-3 py-2">
                <Badge tone={b.priority <= 1 ? 'danger' : b.priority === 2 ? 'warning' : 'info'}>{b.type}</Badge>
                <span className="text-data-md text-on-surface">{b.text}</span>
              </li>
            ))}
          </ul>
        </Block>
      </CardBody>
    </Card>
  );
}

// ─── Lead Scores ──────────────────────────────────────────────────────────────
export function LeadScoresCard() {
  const { data = [], isLoading, isError } = useLeadScores();
  const rows = [...data].sort((a, b) => b.score - a.score);
  const columns: Column<LeadScore>[] = [
    { key: 'name', header: 'Lead', render: (r) => <span className="font-medium text-on-surface">{r.name}</span> },
    { key: 'score', header: 'AI Score', align: 'right', render: (r) => <Badge tone={scoreTone(r.score)}>{r.score}</Badge> },
  ];
  return (
    <Card>
      <CardHeader title="Lead Quality Scores" />
      <CardBody>
        <Block isLoading={isLoading} isError={isError} isEmpty={rows.length === 0} emptyTitle="No scored leads">
          <Table columns={columns} data={rows.slice(0, 50)} rowKey={(r) => r.id} />
        </Block>
      </CardBody>
    </Card>
  );
}

// ─── Deal Intelligence (reuses Deals list + Deals AI-score caches) ─────────────
export function DealIntelCard() {
  const deals = useDeals({});
  const scores = useDealScores();
  const isLoading = deals.isLoading || scores.isLoading;
  const isError = deals.isError || scores.isError;
  const scoreMap = (scores.data ?? {}) as Record<string, number>;
  const rows = (deals.data?.deals ?? [])
    .map((d) => ({ ...d, score: scoreMap[d.id] }))
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1));

  const columns: Column<Deal & { score?: number }>[] = [
    { key: 'title', header: 'Deal', render: (r) => <span className="font-medium text-on-surface">{r.title}</span> },
    { key: 'stage', header: 'Stage', render: (r) => <Badge tone={STAGE_TONES[r.stage] ?? 'neutral'}>{STAGE_LABELS[r.stage] ?? r.stage}</Badge> },
    { key: 'score', header: 'Win Prob.', align: 'right', render: (r) => (typeof r.score === 'number' ? <Badge tone={scoreTone(r.score)}>{r.score}%</Badge> : '—') },
  ];
  return (
    <Card>
      <CardHeader title="Deal Win Probability" />
      <CardBody>
        <Block isLoading={isLoading} isError={isError} isEmpty={rows.length === 0} emptyTitle="No deals to score">
          <Table columns={columns} data={rows} rowKey={(r) => r.id} />
        </Block>
      </CardBody>
    </Card>
  );
}

// ─── Call Optimizer ─────────────────────────────────────────────────────────--
export function CallOptimizerCard() {
  const { data, isLoading, isError } = useCallOptimizer();
  const hourly = data?.hourlyData ?? [];
  const best = data?.bestWindows ?? [];
  return (
    <Card>
      <CardHeader title="Call Time Optimizer" />
      <CardBody className="space-y-3">
        <Block isLoading={isLoading} isError={isError} isEmpty={hourly.length === 0} emptyTitle="No call data yet">
          {best.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {best.map((w) => (
                <Badge key={w.hour} tone="success">{w.label} · {w.successRate}%</Badge>
              ))}
            </div>
          )}
          <BarChart data={hourly} xKey="label" yKey="successRate" color="#6E62FF" height={240} />
        </Block>
      </CardBody>
    </Card>
  );
}

// ─── Niche Intelligence ─────────────────────────────────────────────────────--
export function NicheCard() {
  const { data = [], isLoading, isError } = useNicheIntelligence();
  const columns: Column<NicheRow>[] = [
    { key: 'niche', header: 'Niche', render: (r) => <span className="font-medium text-on-surface">{r.niche}</span> },
    { key: 'leadCount', header: 'Leads', align: 'right', render: (r) => r.leadCount },
    { key: 'talked', header: 'Connected', align: 'right', render: (r) => r.talked },
    { key: 'successRate', header: 'Success', align: 'right', render: (r) => `${r.successRate}%` },
    { key: 'recommendation', header: 'Rec.', render: (r) => <Badge tone={r.recommendation === 'high' ? 'success' : r.recommendation === 'medium' ? 'warning' : 'neutral'}>{r.recommendation}</Badge> },
  ];
  return (
    <Card>
      <CardHeader title="Niche Intelligence" />
      <CardBody>
        <Block isLoading={isLoading} isError={isError} isEmpty={data.length === 0} emptyTitle="No niche data">
          <Table columns={columns} data={data} rowKey={(r) => r.niche} />
        </Block>
      </CardBody>
    </Card>
  );
}

// ─── Scrape Advisor ─────────────────────────────────────────────────────────--
export function ScrapeAdvisorCard() {
  const { data, isLoading, isError } = useScrapeAdvisor();
  const niches = data?.suggestedNiches ?? [];
  const columns: Column<ScrapeNiche>[] = [
    { key: 'niche', header: 'Suggested Niche', render: (r) => <span className="font-medium text-on-surface">{r.niche}</span> },
    { key: 'demand', header: 'Demand', align: 'right', render: (r) => r.demand },
    { key: 'existing', header: 'Existing', align: 'right', render: (r) => r.existing },
    { key: 'gap', header: 'Opportunity', align: 'right', render: (r) => Math.round(r.gap) },
  ];
  return (
    <Card>
      <CardHeader title="Scrape Advisor" />
      <CardBody>
        <Block isLoading={isLoading} isError={isError} isEmpty={niches.length === 0} emptyTitle="No suggestions">
          <Table columns={columns} data={niches} rowKey={(r) => r.niche} />
        </Block>
      </CardBody>
    </Card>
  );
}

// ─── Anomalies (team) ─────────────────────────────────────────────────────────
export function AnomaliesCard() {
  const { data = [], isLoading, isError } = useAnomalies();
  return (
    <Card>
      <CardHeader title="Productivity Anomalies" />
      <CardBody>
        <Block isLoading={isLoading} isError={isError} isEmpty={data.length === 0} emptyTitle="No anomalies detected">
          <ul className="flex flex-col gap-2">
            {data.map((a: AnomalyRow) => (
              <li key={a.userId} className="rounded-DEFAULT border border-outline bg-surface px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-on-surface">{a.name}</span>
                  <Badge tone={a.highestSeverity === 'high' ? 'danger' : 'warning'}>{a.highestSeverity}</Badge>
                </div>
                <ul className="mt-1 list-disc pl-5 text-body-sm text-on-surface-variant">
                  {a.flags.map((f, i) => <li key={i}>{f.message}</li>)}
                </ul>
              </li>
            ))}
          </ul>
        </Block>
      </CardBody>
    </Card>
  );
}
